import { useState } from 'react';
import { Cloud, HardDrive, Link2 } from 'lucide-react';

/**
 * ============================================================================
 * ⭐⭐ WHICH PILE OF SCANS AM I LOOKING AT — SHAREPOINT OR THIS MACHINE
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"on Scanned Paperwork panel, we have two icons, one
 * > showing the Network driver or Sharepoint icon and one showing a local drive
 * > so they can swap the view."*
 *
 * ---- ⛔⛔ THIS REVERSES §1R.16, AND PETE SAID SO EXPLICITLY ---------------
 *
 * Migration 162 said **"only one source is ever consulted"**, because *"a scan
 * arriving from a source the screen says is off is untraceable."*
 *
 * ⭐ THE DANGER THAT RULE NAMED WAS A **SILENT FALLBACK**. This is the
 * opposite: the active source is STATED ON SCREEN IN WORDS at all times, which
 * is why the reversal is safe.
 *
 * ---- ⛔⛔ THE WORDS MOVED. THEY DID NOT GO. (2026-09-10) -------------------
 *
 * > Pete: *"Only the icons, not the whole button."*
 *
 * ⚠️ THIS CONTROL PREVIOUSLY CARRIED THE WORDS ITSELF, and a note here said
 * that reducing it to unlabelled icons would bring §1R.16's objection back.
 * ⭐ THAT NOTE WAS RIGHT ABOUT THE REQUIREMENT AND WRONG ABOUT WHERE IT HAD TO
 * BE MET. The requirement is that the SCREEN says which source is showing, not
 * that this particular control does — and the panel header now reads
 * "Scanned Paperwork · SharePoint" or "· This machine".
 *
 * ⛔ SO THE OBLIGATION FOLLOWED THE WORDS. It is now `PaperworkSliver` and
 * `ScanFolderPanel` that must name the source, and `paperwork-tray-guard.cjs`
 * asserts it there. Removing the word from BOTH places is what would be unsafe;
 * removing it from one and not the other is what happened here on purpose.
 *
 * ⚠️ THE TOOLTIPS STILL SAY IT TOO, so an icon is never a guess even before
 * the eye travels to the header.
 *
 * ---- ⚠️ WHY THE OPERATIONAL NEED IS REAL ---------------------------------
 *
 * A scanner cannot write to SharePoint without an Entra sign-in **per scan**,
 * so sites go scanner → email → Power Automate → SharePoint and wait. Scanning
 * to a local folder is immediate. Forcing one source made the fast path
 * unavailable at a site configured for the slow one (§1AO).
 *
 * ---- ⛔ IT IS A SOURCE SWITCH, NOT A MODE SWITCH --------------------------
 *
 * ⚠️ The tray also has an ATTACHED-PAPERWORK mode, driven by hovering a load.
 * That is a different kind of thing, and three lookalike icons with two
 * meanings is how somebody clicks the wrong one — so the two sources are
 * grouped as one segmented control here, and the attached mode announces
 * itself with a header and a back chip instead of a third icon.
 * ============================================================================
 */

export type ScanSource = 'SharePoint' | 'LocalFolder';

/** ⭐ The three things this tray can be showing. */
export type TrayView = ScanSource | 'Attached';

export function ScanSourceToggle({
    value, onChange, sharePointConfigured = true, localSupported = true,
    attachedAvailable = false, onDropAttached, onDropUnfile, onDropLocal,
}: {
    value: TrayView;
    onChange: (v: TrayView) => void;
    /**
     * ⛔ FALSE WHEN THE SITE HAS NO SHAREPOINT FOLDER SET. The button is shown
     * and disabled rather than hidden: "this site does not use SharePoint" and
     * "the control does not exist" are different facts, and hiding it makes a
     * scheduler hunt for a feature they were told about.
     */
    sharePointConfigured?: boolean;
    /** ⛔ False on Firefox/Safari — the directory picker does not exist there. */
    localSupported?: boolean;
    /**
     * ⛔ FALSE UNTIL SOMETHING HAS BEEN HOVERED. Disabled, never hidden — for
     * the same reason as SharePoint above. ⚠️ A control that appears and
     * disappears is one a scheduler stops trusting is there.
     */
    attachedAvailable?: boolean;
    /**
     * ---- ⭐⭐ DROP A SCAN ON THE ICON TO FILE IT ON THE REMEMBERED LOAD ------
     *
     * > Pete, 2026-09-10: *"Can we drag a document from Scanned Paperwork to the
     * > paperwork toggle button (last appointment, driver in the history)?"*
     *
     * ⭐ IT IS THE SHORTEST ROUTE THERE IS. Filing normally means carrying the
     * scan across the board to a chip that may be scrolled out of view; this
     * target is two inches from the row and never moves.
     *
     * ⛔ IT FILES ONTO WHAT THE ICON ALREADY SHOWS — the load the panel is
     * displaying, named in its header. ⚠️ Nothing hidden is chosen for the
     * scheduler: if the icon is disabled there is no target, and the drop is
     * refused rather than guessed at.
     */
    onDropAttached?: (e: React.DragEvent) => void;
    /**
     * ---- ⭐⭐ DROP A FILED DOCUMENT HERE TO PUT IT BACK IN THE PILE ----------
     *
     * > Pete, 2026-09-10: *"Should be able to move the paperwork to all three
     * > icons, so you can move it back from the Attached Paperwork to the
     * > Scanned Paperwork."*
     *
     * ⭐ THE CLOUD ICON IS THE WAITING PILE, so dropping a document on it means
     * "this is not on that load" — the exact reverse of the Attached icon.
     * Filing was one-way until now; the only undo was VOIDING, which says the
     * document should not exist.
     *
     * ⭐ ALL THREE ICONS ARE TARGETS SINCE 2026-09-10 — see `onDropLocal`.
     */
    onDropUnfile?: (e: React.DragEvent) => void;
    /**
     * ---- ⭐⭐ "THIS MACHINE" TAKES A DROP TOO NOW (2026-09-10) ---------------
     *
     * > Pete: *"Still not letting me drag from sharepoint to local."*
     *
     * ⛔ ~~"The scan folder is opened `mode: 'read'` — a page cannot write to
     * somebody's disk — so this target exists to SAY that, not to act."~~ THE
     * FIRST HALF WAS TRUE AND THE SECOND HALF WAS AN INVENTION. Our handle was
     * read-only; the browser was never the obstacle. `showDirectoryPicker({
     * mode: 'readwrite' })` and `createWritable()` do exactly this, and the
     * folder now opens that way — `lib/scanFolder.ts` carries the whole note.
     *
     * ---- ⛔⛔ AND IT IS A MOVE, NOT A COPY (2026-09-10) --------------------
     *
     * > Pete: *"sometimes it moves, sometimes it copies. Can we always make it
     * > move please?"*
     *
     * ⛔ ~~"IT COPIES OUT OF DOCK, IT DOES NOT MOVE OUT OF IT."~~ That was one
     * icon's opinion, and it disagreed with the rest of the product: `ingestScan`
     * has ALWAYS moved a SharePoint scan into `Filed/YYYY-MM` once Dock had it.
     * ⚠️ So a scheduler dragging between three piles saw two different verbs for
     * one gesture, which is exactly what Pete describes.
     *
     * ⭐ ALL THREE CURSORS NOW SAY `move`, AND ALL THREE ACTUALLY DO. The source
     * is removed only AFTER the destination confirms — see `Calendar`'s handlers
     * and the ordering note on `deleteFile`.
     *
     * ⛔⛔ AND ONE OF THE DELETES IS UNRECOVERABLE. Removing a file from the local
     * scan folder goes through `removeEntry`, which has no Recycle Bin behind it;
     * SharePoint's `deleteScan` is recoverable for 93 days. Same cursor, very
     * different consequence — which is why the local drop confirms first.
     */
    onDropLocal?: (e: React.DragEvent) => void;
}) {
    /** ⭐ The icon says it will take the drop. A target that looks inert is one
        nobody tries twice. */
    const [over, setOver] = useState(false);
    /** ⭐ Separate from `over` — three targets, three highlights. */
    const [overPile, setOverPile] = useState(false);
    /** ⭐ The third one. ⛔ NOT shared with `over`: two icons lighting up at once
        is how a scheduler learns the wrong one accepted the drop. */
    const [overLocal, setOverLocal] = useState(false);
    /**
     * ⚠️ THE ACTIVE ONE IS NOT MERELY A SHADE DARKER. With no words anywhere,
     * this state is the ONLY thing distinguishing three icons — so it gets a
     * filled chip and a full-strength colour, not a subtle tint.
     * ⛔ `aria-pressed` carries the same fact to a screen reader, and the
     * `title` plus an `sr-only` label is what names each one.
     */
    /**
     * ---- ⛔⛔ `aria-disabled`, NEVER THE `disabled` ATTRIBUTE ----------------
     *
     * > Pete, 2026-09-10: *"Not letting me at all move the files now???? To any
     * > of the 3 icons"*.
     *
     * ⛔ A DISABLED <button> RECEIVES NO EVENTS AT ALL — including `dragover` and
     * `drop`. So the Attached icon, disabled until something had been hovered,
     * was not a drop target that refused: it was not a drop target. The cursor
     * said no and nothing could explain why.
     *
     * ⭐ `aria-disabled` KEEPS THE MEANING AND THE EVENTS. A screen reader still
     * hears "unavailable", the look is unchanged, the click is guarded — and the
     * element can now take a drop and SAY what it does with it. An icon that
     * refuses out loud teaches; an inert one just looks broken.
     */
    const btn = (active: boolean, available: boolean) =>
        'flex items-center justify-center rounded p-1 transition-colors '
        + (available ? '' : 'opacity-40 ')
        + (active
            ? 'bg-primary/15 text-primary'
            : 'text-muted-foreground hover:text-foreground');

    return (
        <div className="flex shrink-0 items-center gap-0.5 rounded border px-0.5 py-0.5">
            <button
                type="button"
                onClick={() => { if (sharePointConfigured) onChange('SharePoint'); }}
                aria-disabled={!sharePointConfigured}
                aria-pressed={value === 'SharePoint'}
                title={sharePointConfigured
                    ? 'Scans waiting to be filed — drop a filed document here to put it back'
                    : 'This site has no SharePoint scan folder configured'}
                onDragOver={onDropUnfile
                    ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverPile(true); }
                    : undefined}
                onDragLeave={() => setOverPile(false)}
                onDrop={onDropUnfile
                    ? (e) => { e.preventDefault(); setOverPile(false); onDropUnfile(e); }
                    : undefined}
                className={btn(value === 'SharePoint', sharePointConfigured)
                    + (overPile ? ' ring-2 ring-primary ring-offset-1' : '')}
            >
                <Cloud className="h-3.5 w-3.5" />
                <span className="sr-only">SharePoint</span>
            </button>
            <button
                type="button"
                onClick={() => { if (localSupported) onChange('LocalFolder'); }}
                aria-disabled={!localSupported}
                aria-pressed={value === 'LocalFolder'}
                /**
                 * ⛔⛔ `dropEffect = 'move'`, NOT `'none'`. It was `'none'` while
                 * this icon could only explain itself, and `'none'` is not
                 * decoration — the browser REFUSES THE DROP OUTRIGHT and no `drop`
                 * event is ever delivered. ⚠️ That is the same class of fault as
                 * `effectAllowed = 'copy'` versus a `'move'` drop earlier today:
                 * the gesture dies in the browser, the handler is never called, and
                 * the icon reads as broken with nothing in the console.
                 *
                 * ⭐ AND `'move'` IS THE TRUTHFUL WORD SINCE PETE ASKED FOR IT: the
                 * file lands in the folder and the Dock document is voided, so it
                 * really does leave. ⚠️ The confirm dialog in front of this says so
                 * in our own words — Chrome's permission prompt cannot be restyled,
                 * but it need not be the first thing a scheduler reads.
                 */
                onDragOver={onDropLocal
                    ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverLocal(true); }
                    : undefined}
                onDragLeave={() => setOverLocal(false)}
                onDrop={onDropLocal
                    ? (e) => { e.preventDefault(); setOverLocal(false); onDropLocal(e); }
                    : undefined}
                title={localSupported
                    ? 'Scans in the folder on this machine — drop a document here to move it out of Dock and into that folder'
                    : 'This browser cannot read a local folder — Chrome or Edge only'}
                className={btn(value === 'LocalFolder', localSupported)
                    + (overLocal ? ' ring-2 ring-primary ring-offset-1' : '')}
            >
                <HardDrive className="h-3.5 w-3.5" />
                <span className="sr-only">This machine</span>
            </button>
            {/**
              * ---- ⭐⭐ THE THIRD ICON, BESIDE THE OTHER TWO --------------------
              *
              * > Pete, 2026-09-10: *"need one more icon for the toggle between
              * > Scanned paperwork and Attached Paperwork. Right next to the
              * > Sharepoint icon - not how much clearer I can be."*
              *
              * ⛔ IT REPLACES THE "← Scanned" CHIP that used to appear only while
              * attached. ⚠️ That chip was a way BACK and not a way IN — there was
              * no control that took you TO the attached view, so the only route
              * was to hover something and hope. Three peers, always present, is
              * one model instead of two half-models.
              */}
            <button
                type="button"
                onClick={() => { if (attachedAvailable) onChange('Attached'); }}
                aria-disabled={!attachedAvailable}
                aria-pressed={value === 'Attached'}
                title={attachedAvailable
                    ? 'Paperwork on the appointment you last pointed at — drop a scan here to file it on that appointment'
                    : 'Hover an appointment or a driver first — then this shows its paperwork'}
                /* ⛔ ONLY WHEN THERE IS SOMEWHERE TO PUT IT. Accepting a drop with
                   no target would file a document onto nothing, silently. */
                /* ⛔ ACCEPTS THE GESTURE EVEN WITH NO TARGET YET. The drop then
                   says "point at an appointment first" — see Calendar's handler. Refusing
                   silently is what made this read as broken. */
                onDragOver={onDropAttached
                    ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(true); }
                    : undefined}
                onDragLeave={() => setOver(false)}
                onDrop={onDropAttached
                    ? (e) => { e.preventDefault(); setOver(false); onDropAttached(e); }
                    : undefined}
                className={btn(value === 'Attached', attachedAvailable)
                    + (over ? ' ring-2 ring-primary ring-offset-1' : '')}
            >
                <Link2 className="h-3.5 w-3.5" />
                <span className="sr-only">Attached paperwork</span>
            </button>
        </div>
    );
}
